import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, Heart, Wind, Anchor, RefreshCcw, BookOpen, Sparkles, Eye, Hand, Ear, Flower2, Apple } from "lucide-react";

// Mammoth Love palette tokens used across the daily-rep modals.
//   bg/text are landing's base (#F5F5F0 / #0e203b); mint #9bd5d7 is the
//   accent that already lives on the rep card on hover, so the modal feels
//   continuous with the click action.
const C = {
  bg: "#F5F5F0",
  text: "#0e203b",
  accent: "#9bd5d7",
  surface: "#FFFFFF",
};

/* ────────────────────────────────────────────────────────────────────── */
/*  Tiny in-modal "session report" previews. Pure SVG/CSS, no chart libs. */
/* ────────────────────────────────────────────────────────────────────── */

const PEACH = "#feb3a6";
export { PEACH };

/**
 * Shared keyframes for all previews. Mounted once at modal level so every
 * preview can re-use `.ml-stat-pop` without re-injecting the CSS.
 *  - mlStatPop  : a soft springy fade-in-up used on stat pills / badges
 *  - mlFadeIn   : a plain fade used on supporting elements
 */
const SharedPreviewStyles = () => (
  <style>{`
    /* ─── shared "calm tempo" tokens ─────────────────────────────────── */
    /*   --ml-curve : easeOutQuint — gentle settle, no overshoot         */
    /*   line/area use linear so the *whole* draw lingers, not just end  */
    /* ──────────────────────────────────────────────────────────────── */
    @keyframes mlStatPop {
      0%   { opacity: 0; transform: translateY(8px) scale(0.96); }
      100% { opacity: 1; transform: translateY(0)   scale(1);    }
    }
    @keyframes mlFadeIn {
      0%   { opacity: 0; transform: translateY(4px); }
      100% { opacity: 1; transform: translateY(0);   }
    }
    @keyframes mlMoodDrawLine {
      from { stroke-dashoffset: 1; }
      to   { stroke-dashoffset: 0; }
    }
    @keyframes mlMoodRevealArea {
      from { clip-path: inset(0 100% 0 0); }
      to   { clip-path: inset(0 0% 0 0);   }
    }
    @keyframes mlMarkerIn {
      0%   { opacity: 0; transform: translateY(4px); }
      100% { opacity: 1; transform: translateY(0);   }
    }
    @keyframes mlPeakPulse {
      0%, 100% { transform: scale(1);    opacity: 0.30; }
      50%      { transform: scale(1.55); opacity: 0;    }
    }
    @keyframes mlPeakCore {
      0%, 100% { transform: scale(1);    }
      50%      { transform: scale(1.10); }
    }
    @keyframes mlPeachPulse {
      0%, 100% { box-shadow: 0 0 0 0 ${PEACH}aa; transform: scale(1);    }
      50%      { box-shadow: 0 0 0 9px ${PEACH}00; transform: scale(1.16); }
    }
    @keyframes mlEcgTrace {
      0%   { stroke-dashoffset: 115; }
      100% { stroke-dashoffset: -15; }
    }
    /* checkmark life-sync: tracer arrives at ✓ near the end of the loop,
       then the ✓ slowly returns to normal while the tracer resets from
       the word side. Crossing 100%→0% the size stays 1.4 then eases back
       to 1 across the first 30% of the next cycle. */
    @keyframes mlGroundCheck {
      0%   { transform: scale(1.4); font-weight: 900; }
      30%  { transform: scale(1);   font-weight: 700; }
      75%  { transform: scale(1);   font-weight: 700; }
      90%  { transform: scale(1.4); font-weight: 900; }
      100% { transform: scale(1.4); font-weight: 900; }
    }
    /* cooler-thought "rewire arrival" — the card subtly blooms each time
       the vertical tracer reaches the bottom, then settles back during
       the next tracer's descent from the Hot card. */
    @keyframes mlCoolerBloom {
      0%   { transform: scale(1.025); box-shadow: 0 0 0 4px ${C.accent}66; }
      30%  { transform: scale(1);     box-shadow: 0 0 0 0   ${C.accent}00; }
      75%  { transform: scale(1);     box-shadow: 0 0 0 0   ${C.accent}00; }
      90%  { transform: scale(1.025); box-shadow: 0 0 0 4px ${C.accent}66; }
      100% { transform: scale(1.025); box-shadow: 0 0 0 4px ${C.accent}66; }
    }
    /* "kept private" stamp bloom — fires when the journal tracer arrives,
       then fades during the next tracer's traversal back from the word
       count side. Mint chip background appears beneath the badge text. */
    @keyframes mlPrivateStamp {
      0%   { transform: scale(1.06); background-color: ${C.accent}66; }
      30%  { transform: scale(1);    background-color: ${C.accent}00; }
      75%  { transform: scale(1);    background-color: ${C.accent}00; }
      90%  { transform: scale(1.06); background-color: ${C.accent}66; }
      100% { transform: scale(1.06); background-color: ${C.accent}66; }
    }
    /* shared tempo — every badge / pill / banner exhales on this curve */
    .ml-stat-pop { animation: mlStatPop 0.95s cubic-bezier(0.22, 1, 0.36, 1) both; }
    .ml-fade-in  { animation: mlFadeIn 0.55s ease-out both; }
    /* the line + area linger at a single constant pace — no rush, no slam */
    .ml-mood-line {
      stroke-dasharray: 1;
      stroke-dashoffset: 1;
      animation: mlMoodDrawLine 3s linear 0.3s forwards;
    }
    .ml-mood-area {
      clip-path: inset(0 100% 0 0);
      animation: mlMoodRevealArea 3s linear 0.3s forwards;
    }
    .ml-mood-marker { animation: mlMarkerIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) both; }
    .ml-mood-avgline { animation: mlFadeIn 0.7s ease-out 3.4s both; }
    /* peak-node reward beacon — keeps pulsing after the chart finishes */
    .ml-mood-peak-halo {
      transform-origin: center;
      transform-box: fill-box;
      animation: mlPeakPulse 2.4s ease-in-out 3s infinite;
    }
    .ml-mood-peak-core {
      transform-origin: center;
      transform-box: fill-box;
      animation: mlPeakCore 2.4s ease-in-out 3s infinite;
    }
    /* peach pulse — pending banners + grounding missing-rep dot */
    .ml-peach-pulse  { animation: mlPeachPulse 2.4s ease-in-out infinite; }
    .ml-ground-pulse { animation: mlPeachPulse 2.4s ease-in-out infinite; }
    /* grounding ECG life-line + sync'd ✓ */
    .ml-ecg-trace {
      stroke-dasharray: 15 100;
      stroke-dashoffset: 115;
      animation: mlEcgTrace 2.4s linear infinite;
    }
    .ml-ground-check {
      display: inline-block;
      transform-origin: center;
      animation: mlGroundCheck 2.4s ease-in-out infinite;
    }
    .ml-cooler-bloom {
      transform-origin: center;
      animation: mlCoolerBloom 2.4s ease-in-out infinite;
    }
    .ml-private-stamp {
      display: inline-block;
      transform-origin: center;
      animation: mlPrivateStamp 2.4s ease-in-out infinite;
    }
    @media (prefers-reduced-motion: reduce) {
      .ml-stat-pop, .ml-fade-in,
      .ml-mood-line, .ml-mood-area, .ml-mood-marker, .ml-mood-avgline,
      .ml-mood-peak-halo, .ml-mood-peak-core,
      .ml-peach-pulse, .ml-ground-pulse,
      .ml-ecg-trace, .ml-ground-check, .ml-cooler-bloom, .ml-private-stamp {
        animation: none !important;
        stroke-dashoffset: 0 !important;
        clip-path: none !important;
        opacity: 1 !important;
      }
    }
  `}</style>
);
export { SharedPreviewStyles };
const PendingRepBanner = ({ label, caption }) => (
  <div
    className="mt-3 flex items-center gap-3 rounded-xl px-3 py-2"
    style={{ backgroundColor: `${PEACH}1f`, border: `1px solid ${PEACH}66` }}
  >
    <span
      className="ml-peach-pulse inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
      style={{ backgroundColor: PEACH }}
    />
    <div className="flex-1 min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: C.text, opacity: 0.55 }}>
        {label}
      </div>
      <div className="text-sm font-bold leading-tight" style={{ color: C.text }}>
        {caption}
      </div>
    </div>
  </div>
);

const MoodPreview = () => {
  const data = [
    { d: "Mon", v: 3.2 },
    { d: "Tue", v: 4.4 },
    { d: "Wed", v: 5.5 },
    { d: "Thu", v: 5.0 },
    { d: "Fri", v: 7.1 },
    { d: "Sat", v: 8.5 },
    { d: "Sun", v: 7.8 },
  ];
  const colorFor = (v) =>
    v >= 8 ? "#9bd5d7" : v >= 6 ? "#A5D610" : v >= 4 ? "#E8E0D5" : "#FFB5A7";

  // Geometry
  const W = 600, H = 210, padL = 38, padR = 14, padT = 30, padB = 30;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const stepX = innerW / (data.length - 1);
  const xFor = (i) => padL + i * stepX;
  const yFor = (v) => padT + innerH - (v / 10) * innerH;
  const points = data.map((d, i) => [xFor(i), yFor(d.v)]);

  // Smooth bezier line through the points
  const smoothPath = (pts) => {
    if (pts.length < 2) return "";
    const path = [`M ${pts[0][0]} ${pts[0][1]}`];
    for (let i = 0; i < pts.length - 1; i++) {
      const [x1, y1] = pts[i];
      const [x2, y2] = pts[i + 1];
      const cpx = (x1 + x2) / 2;
      path.push(`C ${cpx} ${y1}, ${cpx} ${y2}, ${x2} ${y2}`);
    }
    return path.join(" ");
  };
  const linePath = smoothPath(points);
  const baseY = padT + innerH;
  const areaPath = `${linePath} L ${xFor(data.length - 1)} ${baseY} L ${padL} ${baseY} Z`;

  const avg = (data.reduce((s, x) => s + x.v, 0) / data.length).toFixed(1);
  const lift = (data[data.length - 1].v - data[0].v).toFixed(1);
  const peak = data.reduce((a, b) => (b.v > a.v ? b : a));

  return (
    <div className="rounded-2xl p-5" style={{ backgroundColor: C.surface, border: `1px solid ${C.text}10` }}>
      {/* Header + stat pills */}
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] opacity-60">7-day mood report</div>
          <div
            className="font-display font-black text-2xl mt-1 leading-none ml-stat-pop"
            style={{ animationDelay: "0ms" }}
          >
            Trending up <span style={{ color: C.accent }}>↑</span>
          </div>
        </div>
        <div className="flex gap-2">
          <div
            className="rounded-xl px-3 py-2 text-center ml-stat-pop"
            style={{ backgroundColor: `${C.text}08`, animationDelay: "320ms" }}
          >
            <div className="text-[9px] uppercase tracking-widest opacity-50 font-bold">Avg</div>
            <div className="font-display font-black text-lg leading-none mt-0.5">{avg}</div>
          </div>
          <div
            className="rounded-xl px-3 py-2 text-center ml-stat-pop"
            style={{ backgroundColor: `${C.accent}40`, animationDelay: "640ms" }}
          >
            <div className="text-[9px] uppercase tracking-widest opacity-60 font-bold">Lift</div>
            <div className="font-display font-black text-lg leading-none mt-0.5" style={{ color: C.text }}>+{lift}</div>
          </div>
          <div
            className="rounded-xl px-3 py-2 text-center ml-stat-pop"
            style={{ backgroundColor: `${C.text}08`, animationDelay: "960ms" }}
          >
            <div className="text-[9px] uppercase tracking-widest opacity-50 font-bold">Peak</div>
            <div className="font-display font-black text-lg leading-none mt-0.5">{peak.d}</div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: "auto", display: "block" }} role="img" aria-label="7-day mood line chart trending upward">
        <defs>
          <linearGradient id="moodArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.accent} stopOpacity="0.55" />
            <stop offset="100%" stopColor={C.accent} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* horizontal grid + y labels */}
        {[0, 2.5, 5, 7.5, 10].map((g) => (
          <g key={g}>
            <line
              x1={padL} x2={W - padR}
              y1={yFor(g)} y2={yFor(g)}
              stroke={C.text} strokeOpacity="0.08" strokeDasharray="2 4"
            />
            <text
              x={padL - 8} y={yFor(g) + 3}
              fontSize="9" textAnchor="end"
              fill={C.text} fillOpacity="0.4" fontWeight="700"
            >
              {g}
            </text>
          </g>
        ))}

        {/* vertical day ticks */}
        {data.map((d, i) => (
          <line
            key={`v-${d.d}`}
            x1={xFor(i)} x2={xFor(i)}
            y1={padT} y2={baseY}
            stroke={C.text} strokeOpacity="0.04"
          />
        ))}

        {/* area fill */}
        <path d={areaPath} fill="url(#moodArea)" className="ml-mood-area" />

        {/* main line */}
        <path
          d={linePath}
          pathLength="1"
          fill="none"
          stroke={C.text}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="ml-mood-line"
        />

        {/* avg dashed reference line */}
        <g className="ml-mood-avgline">
          <line
            x1={padL} x2={W - padR}
            y1={yFor(parseFloat(avg))} y2={yFor(parseFloat(avg))}
            stroke={C.text} strokeOpacity="0.35"
            strokeDasharray="4 4" strokeWidth="1"
          />
          <text
            x={W - padR} y={yFor(parseFloat(avg)) - 5}
            fontSize="9" textAnchor="end"
            fill={C.text} fillOpacity="0.55" fontWeight="700"
          >
            avg {avg}
          </text>
        </g>

        {/* markers + value bubbles + day labels */}
        {data.map((d, i) => {
          const [x, y] = points[i];
          const col = colorFor(d.v);
          const isPeak = d.d === peak.d;
          return (
            <g
              key={d.d}
              className="ml-mood-marker"
              style={{ animationDelay: `${1.0 + i * 0.22}s` }}
            >
              {isPeak && (
                <circle className="ml-mood-peak-halo" cx={x} cy={y} r="14" fill={col} fillOpacity="0.30" />
              )}
              <circle
                className={isPeak ? "ml-mood-peak-core" : undefined}
                cx={x} cy={y}
                r={isPeak ? 8 : 6}
                fill={col} stroke={C.surface} strokeWidth="2.5"
              />
              <text
                x={x} y={y - 14}
                fontSize="10" textAnchor="middle"
                fill={C.text} fillOpacity="0.75" fontWeight="700"
              >
                {d.v.toFixed(1)}
              </text>
              <text
                x={x} y={baseY + 18}
                fontSize="11" textAnchor="middle"
                fill={C.text} fillOpacity={isPeak ? 0.9 : 0.55}
                fontWeight="700"
              >
                {d.d}
              </text>
            </g>
          );
        })}
      </svg>

      {/* mood band legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-[10px] font-bold uppercase tracking-wider opacity-70">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#FFB5A7" }} />Heavy
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#E8E0D5" }} />Okay
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#A5D610" }} />Lifted
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#9bd5d7" }} />Clear
        </span>
      </div>
      <PendingRepBanner label="Today" caption="Your check-in is waiting · 30 seconds" />
    </div>
  );
};

const BreathingPreview = () => (
  <div className="rounded-2xl p-5" style={{ backgroundColor: C.surface, border: `1px solid ${C.text}10` }}>
    {/* keyframes scoped to this preview */}
    <style>{`
      @keyframes mlBreathTrace {
        0%   { stroke-dashoffset: 272; }
        50%  { stroke-dashoffset: 0; }
        100% { stroke-dashoffset: -272; }
      }
      @keyframes mlBreathHalo {
        0%, 100% { transform: scale(1);    opacity: 0.35; }
        50%      { transform: scale(1.14); opacity: 0.65; }
      }
      @keyframes mlBreathCount {
        0%, 100% { transform: scale(1);   opacity: 0.9; }
        50%      { transform: scale(1.08); opacity: 1; }
      }
      @keyframes mlBreathDot {
        0%, 100% { transform: scale(1); opacity: 1; }
        50%      { transform: scale(0.4); opacity: 0.4; }
      }
      .ml-breath-trace { animation: mlBreathTrace 8s linear infinite; }
      .ml-breath-halo  {
        animation: mlBreathHalo 4s ease-in-out infinite;
        transform-origin: center;
        transform-box: fill-box;
      }
      .ml-breath-count { animation: mlBreathCount 4s ease-in-out infinite; }
      .ml-breath-dot   { animation: mlBreathDot 1.6s ease-in-out infinite; }
      @media (prefers-reduced-motion: reduce) {
        .ml-breath-trace, .ml-breath-halo, .ml-breath-count, .ml-breath-dot {
          animation: none !important;
        }
      }
    `}</style>

    <div className="flex items-center justify-between mb-4">
      <span className="text-[11px] font-bold uppercase tracking-[0.22em] opacity-60">Today · Box 4·4·4·4</span>
      <span className="text-[11px] font-bold uppercase tracking-[0.22em] ml-stat-pop" style={{ color: C.accent, animationDelay: "320ms" }}>calm score</span>
    </div>

    <div className="flex items-center gap-5">
      <div className="relative flex-shrink-0" style={{ width: 120, height: 120 }}>
        <svg width="120" height="120" viewBox="0 0 96 96" aria-hidden="true">
          {/* soft pulsing halo */}
          <circle className="ml-breath-halo" cx="48" cy="48" r="42" fill={`${C.accent}55`} />
          {/* static track */}
          <rect
            x="14" y="14" width="68" height="68" rx="14"
            fill="none" stroke={C.text} strokeOpacity="0.12" strokeWidth="6"
          />
          {/* animated tracer — loops continuously around the box */}
          <rect
            className="ml-breath-trace"
            x="14" y="14" width="68" height="68" rx="14"
            fill="none" stroke={C.accent} strokeWidth="6"
            strokeDasharray="272" strokeLinecap="round"
            transform="rotate(-90 48 48)"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="ml-breath-count font-display font-black"
            style={{ color: C.text, fontSize: "2rem", lineHeight: 1, display: "inline-block", transformOrigin: "center" }}
          >
            12
          </span>
        </div>
      </div>

      <div className="flex-1 space-y-2">
        <div className="flex justify-between ml-stat-pop" style={{ animationDelay: "320ms" }}>
          <span className="text-sm opacity-70">Cycles</span><span className="font-bold text-sm">12</span>
        </div>
        <div className="flex justify-between ml-stat-pop" style={{ animationDelay: "640ms" }}>
          <span className="text-sm opacity-70">Time</span><span className="font-bold text-sm">4:00</span>
        </div>
        <div className="flex justify-between ml-stat-pop" style={{ animationDelay: "960ms" }}>
          <span className="text-sm opacity-70">Pulse drop</span><span className="font-bold text-sm" style={{ color: C.accent }}>−28%</span>
        </div>
        <div
          className="flex items-center gap-1.5 pt-1 text-[10px] font-bold uppercase tracking-widest ml-stat-pop"
          style={{ color: C.text, opacity: 0.7, animationDelay: "1280ms" }}
        >
          <span
            className="ml-breath-dot inline-block w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: C.accent }}
          />
          <span>Live · syncing breath</span>
        </div>
      </div>
    </div>
  </div>
);

const GroundingPreview = () => {
  const items = [
    { n: 5, label: "See",   done: true,  Icon: Eye },
    { n: 4, label: "Touch", done: true,  Icon: Hand },
    { n: 3, label: "Hear",  done: true,  Icon: Ear },
    { n: 2, label: "Smell", done: true,  Icon: Flower2 },
    { n: 1, label: "Taste", done: false, Icon: Apple },
  ];
  return (
    <div className="rounded-2xl p-5" style={{ backgroundColor: C.surface, border: `1px solid ${C.text}10` }}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] opacity-60">Senses anchor</span>
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] ml-stat-pop" style={{ color: C.accent, animationDelay: "320ms" }}>4/5 complete</span>
      </div>
      <div className="space-y-2.5">
        {items.map(({ n, label, done, Icon }, idx) => {
          const phaseDelay = `${-idx * 0.6}s`;
          return (
            <div key={n} className="flex items-center gap-3 py-1.5">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-display font-black text-sm flex-shrink-0 ${!done ? "ml-ground-pulse" : ""}`}
                style={{
                  backgroundColor: done ? C.accent : PEACH,
                  color: C.text,
                }}
              >
                {n}
              </div>
              <span className="font-semibold" style={{ opacity: done ? 1 : 0.65 }}>{label}</span>
              {done ? (
                <>
                  <div className="flex-1 min-w-0 mx-2 self-stretch flex items-center" aria-hidden="true">
                    <svg
                      viewBox="0 0 100 20"
                      preserveAspectRatio="none"
                      style={{ width: "100%", height: 20, display: "block" }}
                    >
                      {/* faded baseline */}
                      <path
                        d="M 0,10 L 30,10 L 34,12 L 38,4 L 42,16 L 46,10 L 100,10"
                        fill="none"
                        stroke={`${C.text}14`}
                        strokeWidth="1"
                      />
                      {/* animated life-line tracer */}
                      <path
                        d="M 0,10 L 30,10 L 34,12 L 38,4 L 42,16 L 46,10 L 100,10"
                        pathLength="100"
                        fill="none"
                        stroke={PEACH}
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="ml-ecg-trace"
                        style={{ animationDelay: phaseDelay }}
                      />
                    </svg>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className="ml-ground-check text-sm font-bold uppercase tracking-wider"
                      style={{ color: C.accent, animationDelay: phaseDelay }}
                    >
                      ✓
                    </span>
                    <Icon size={22} strokeWidth={2.5} style={{ color: PEACH }} />
                  </div>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ReframePreview = () => (
  <div className="rounded-2xl p-5" style={{ backgroundColor: C.surface, border: `1px solid ${C.text}10` }}>
    <div className="flex items-center justify-between mb-4">
      <span className="text-[11px] font-bold uppercase tracking-[0.22em] opacity-60">This week · 4 reframes</span>
      <span className="text-[11px] font-bold uppercase tracking-[0.22em] ml-stat-pop" style={{ color: C.accent, animationDelay: "320ms" }}>cognitive flex</span>
    </div>
    <div className="space-y-3">
      <div className="rounded-xl p-3" style={{ backgroundColor: `${C.text}06` }}>
        <div className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-1">Hot thought</div>
        <div className="text-sm italic">"I always mess this up."</div>
      </div>

      {/* vertical ECG connector — tracer travels Hot → Cooler */}
      <div className="flex flex-col items-center" aria-hidden="true">
        <svg
          viewBox="0 0 24 60"
          preserveAspectRatio="none"
          style={{ width: 24, height: 60, display: "block" }}
        >
          {/* faded baseline */}
          <path
            d="M 12,0 L 12,18 L 9,20 L 18,25 L 4,30 L 12,33 L 12,60"
            fill="none"
            stroke={`${C.text}14`}
            strokeWidth="1"
          />
          {/* animated rewire tracer */}
          <path
            d="M 12,0 L 12,18 L 9,20 L 18,25 L 4,30 L 12,33 L 12,60"
            pathLength="100"
            fill="none"
            stroke={PEACH}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="ml-ecg-trace"
          />
        </svg>
        <div className="text-[9px] font-bold uppercase tracking-[0.28em] opacity-55 mt-1" style={{ color: C.text }}>
          rewire
        </div>
      </div>

      <div
        className="rounded-xl p-3 ml-cooler-bloom"
        style={{ backgroundColor: `${C.accent}33`, border: `1px solid ${C.accent}` }}
      >
        <div className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-1">Cooler thought</div>
        <div className="text-sm">"One miss isn't a pattern. I've handled this before."</div>
      </div>
    </div>
    <PendingRepBanner label="Today" caption="Your reframe is pending · 3 minutes" />
  </div>
);

const JournalPreview = () => (
  <div className="rounded-2xl p-5" style={{ backgroundColor: C.surface, border: `1px solid ${C.text}10` }}>
    <div className="flex items-center justify-between mb-4">
      <span className="text-[11px] font-bold uppercase tracking-[0.22em] opacity-60">Last entry · Wed</span>
      <span className="text-[11px] font-bold uppercase tracking-[0.22em] ml-stat-pop" style={{ color: C.accent, animationDelay: "320ms" }}>🔥 6-day streak</span>
    </div>
    <p className="text-sm leading-relaxed" style={{ color: `${C.text}cc` }}>
      Today felt heavy at first but I noticed it instead of pushing through. The walk after lunch reset
      something. <span className="font-bold" style={{ color: C.text }}>I'm not behind — I'm pacing.</span>
    </p>
    <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t" style={{ borderColor: `${C.text}10` }}>
      <span className="text-xs opacity-60 font-semibold flex-shrink-0">142 words</span>
      <div className="flex-1 min-w-0 mx-2 self-stretch flex items-center" aria-hidden="true">
        <svg
          viewBox="0 0 100 20"
          preserveAspectRatio="none"
          style={{ width: "100%", height: 20, display: "block" }}
        >
          <path
            d="M 0,10 L 30,10 L 34,12 L 38,4 L 42,16 L 46,10 L 100,10"
            fill="none"
            stroke={`${C.text}14`}
            strokeWidth="1"
          />
          <path
            d="M 0,10 L 30,10 L 34,12 L 38,4 L 42,16 L 46,10 L 100,10"
            pathLength="100"
            fill="none"
            stroke={PEACH}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="ml-ecg-trace"
          />
        </svg>
      </div>
      <span
        className="ml-private-stamp text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 flex-shrink-0"
        style={{ color: C.text }}
      >
        kept private
      </span>
    </div>
    <PendingRepBanner label="Tonight" caption="Tonight's page is blank · waiting for you" />
  </div>
);

/* ────────────────────────────────────────────────────────────────────── */
/*  Per-rep config: icon, copy, preview, encouragement.                   */
/* ────────────────────────────────────────────────────────────────────── */
const REPS = {
  mood: {
    icon: Heart,
    title: "Mood check-in",
    kicker: "Daily rep · 30 seconds",
    overview:
      "A quick, honest read on how you actually are right now — not how you're supposed to be. One tap logs the feeling, one optional line catches the why. Over days it stops being a check-in and starts being a map.",
    Preview: MoodPreview,
    encouragement:
      "You can't change what you can't name. Every check-in is a small act of self-respect — a tiny vote for the version of you that gets to feel known. Start your first 30-second rep today and watch your inner weather become legible.",
  },
  breathing: {
    icon: Wind,
    title: "Box breathing",
    kicker: "Daily rep · 4 minutes",
    overview:
      "A 4·4·4·4 pattern your nervous system already knows how to follow — in, hold, out, hold. We pace it visually so your body leads and your mind catches up. It's not woo; it's vagus nerve, measured.",
    Preview: BreathingPreview,
    encouragement:
      "Four minutes is shorter than the spiral. Train this and you'll have a reset button you can press in traffic, before texts, before tears. Sign in and let your first session teach your body the way home.",
  },
  grounding: {
    icon: Anchor,
    title: "5-4-3-2-1 grounding",
    kicker: "Daily rep · 2 minutes",
    overview:
      "When your brain is in tomorrow's worry or yesterday's regret, your senses are the rope back. Five things you see, four you can touch, three you hear, two you smell, one you taste. It's almost embarrassingly simple. That's the point.",
    Preview: GroundingPreview,
    encouragement:
      "Panic shrinks the world to a thought loop. Grounding gives you back the room you're already in. Start training and your future self will have somewhere safe to land — every single time.",
  },
  reframes: {
    icon: RefreshCcw,
    title: "CBT reframes",
    kicker: "Daily rep · 3 minutes",
    overview:
      "Catch a hot thought, name the distortion, write a cooler one beside it. Not toxic positivity — accurate compassion. Over weeks, the cooler thoughts start arriving first.",
    Preview: ReframePreview,
    encouragement:
      "Your thoughts aren't facts. They're weather — and you can learn to read them. One reframe a day rewires the loop that's been running you. Sign in, train the flex, watch your inner critic become a better editor.",
  },
  journal: {
    icon: BookOpen,
    title: "Journal",
    kicker: "Daily rep · 2 to 10 minutes",
    overview:
      "A private page that doesn't grade you. Prompts when you're blank, silence when you're not. The coach reads only what you ask it to — and only when you ask.",
    Preview: JournalPreview,
    encouragement:
      "The page is the cheapest therapist in the world and it never gets tired. Write the thing you can't say out loud yet and watch it lose its grip. Start your streak tonight — your future self is already grateful.",
  },
};

/* ────────────────────────────────────────────────────────────────────── */
/*  The modal itself.                                                     */
/* ────────────────────────────────────────────────────────────────────── */
export const RepDetailModal = ({ repKey, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!repKey) return null;
  const rep = REPS[repKey];
  if (!rep) return null;
  const Icon = rep.icon;
  const Preview = rep.Preview;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`rep-modal-title-${repKey}`}
      data-testid={`rep-modal-${repKey}`}
      className="fixed inset-0 z-50 overflow-y-auto flex items-start sm:items-center justify-center px-4 py-10"
      style={{ backgroundColor: `${C.bg}f2`, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", color: C.text, animation: "fadeUp 0.35s ease-out both" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl rounded-3xl"
        style={{ backgroundColor: C.surface, boxShadow: "0 30px 80px rgba(14, 32, 59, 0.18)", border: `1px solid ${C.text}10` }}
      >
        {/* Close pill */}
        <button
          onClick={onClose}
          data-testid={`rep-modal-close-${repKey}`}
          aria-label="Close"
          className="absolute right-4 top-4 sm:right-5 sm:top-5 flex items-center gap-2 rounded-full font-bold uppercase tracking-widest transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{
            backgroundColor: C.accent, color: C.text,
            paddingInline: "clamp(12px, 1.4vw, 18px)",
            height: "clamp(36px, 4vw, 44px)",
            fontSize: "clamp(10px, 1vw, 12px)",
            letterSpacing: "0.18em",
          }}
        >
          <X size={14} strokeWidth={3} />
          <span className="hidden sm:inline">Close</span>
        </button>

        <div className="p-6 sm:p-10">
          <SharedPreviewStyles />
          {/* Header */}
          <div className="flex items-start gap-4 pr-20 sm:pr-24">
            <div
              className="rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: C.accent, color: C.text, width: "clamp(56px, 6vw, 72px)", height: "clamp(56px, 6vw, 72px)" }}
            >
              <Icon size={28} strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.28em] opacity-60">{rep.kicker}</div>
              <h2
                id={`rep-modal-title-${repKey}`}
                className="font-display font-black tracking-tighter leading-[0.95] mt-1"
                style={{ fontSize: "clamp(2rem, 5vw, 3.25rem)" }}
              >
                {rep.title}
              </h2>
            </div>
          </div>

          {/* Overview */}
          <div className="mt-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] opacity-60 mb-3">What this rep is</div>
            <p style={{ fontSize: "clamp(1rem, 1.5vw, 1.125rem)", lineHeight: 1.6, color: `${C.text}d9` }}>
              {rep.overview}
            </p>
          </div>

          {/* Visual preview */}
          <div className="mt-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] opacity-60 mb-3">A peek at your session report</div>
            <Preview />
          </div>

          {/* Encouragement */}
          <div
            className="mt-8 rounded-2xl p-5 sm:p-6 flex items-start gap-4"
            style={{ backgroundColor: `${C.accent}33`, border: `1px solid ${C.accent}` }}
          >
            <Sparkles size={22} className="flex-shrink-0 mt-0.5" style={{ color: C.text }} />
            <p className="font-display italic leading-snug" style={{ fontSize: "clamp(1rem, 1.7vw, 1.2rem)" }}>
              {rep.encouragement}
            </p>
          </div>

          {/* CTA row */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <Link
              to="/auth?mode=signup"
              data-testid={`rep-modal-cta-signup-${repKey}`}
              className="btn-pill rounded-full font-bold flex items-center justify-center gap-2"
              style={{
                backgroundColor: C.text, color: C.surface,
                paddingInline: "clamp(20px, 2.4vw, 28px)",
                height: "clamp(48px, 5vw, 56px)",
                fontSize: "clamp(13px, 1.1vw, 15px)",
              }}
            >
              Start training <ArrowRight size={16} />
            </Link>
            <Link
              to="/auth"
              data-testid={`rep-modal-cta-login-${repKey}`}
              className="text-sm font-bold underline underline-offset-4 opacity-70 hover:opacity-100 transition-opacity text-center sm:text-left"
            >
              I already have an account →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepDetailModal;
